import type { SqlDriver, SqlValue, Statement } from '../driver';
import type { OpenResult, WorkerRequest, WorkerResponse } from './protocol';

type Pending = { resolve: (v: unknown) => void; reject: (e: Error) => void };
type Req = WorkerRequest extends infer R
  ? R extends { id: number }
    ? Omit<R, 'id'>
    : never
  : never;

/** SqlDriver talking to the sqlite-wasm worker. Pass the worker from the app's bundler. */
export async function createWorkerDriver(
  worker: Worker,
  name = 'nexus',
): Promise<SqlDriver & { reason?: string }> {
  let nextId = 1;
  const pending = new Map<number, Pending>();
  worker.onmessage = (ev: MessageEvent<WorkerResponse>) => {
    const res = ev.data;
    const p = pending.get(res.id);
    if (!p) return;
    pending.delete(res.id);
    if (res.ok) p.resolve(res.result);
    else p.reject(new Error(res.error));
  };
  const call = <T>(req: Req): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      const id = nextId++;
      pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      worker.postMessage({ ...req, id });
    });

  const opened = await call<OpenResult>({ op: 'open', name });
  return {
    persistent: opened.persistent,
    ...(opened.reason ? { reason: opened.reason } : {}),
    query: <T>(sql: string, params?: SqlValue[]) =>
      call<T[]>(params ? { op: 'query', sql, params } : { op: 'query', sql }),
    batch: (statements: Statement[]) => call<void>({ op: 'batch', statements }),
    close: async () => {
      await call<void>({ op: 'close' });
      worker.terminate();
    },
    exportDb: () => call<Uint8Array>({ op: 'export' }),
    importDb: (bytes: Uint8Array) => call<void>({ op: 'import', bytes }),
  };
}
